// Onboarding flow tracking subjects
export const TRACKING_LANDING_PAGE_CTA = 'landing_page_cta_clicked';
export const TRACKING_VISITED_LOGIN = 'visited_login_route';
export const TRACKING_START_SIGN_UP = 'signup_start';
// The questions have their own routes, so a step can be returned to. Its page
// view still reports once per visit, which is what the funnel counted when
// they shared one URL, and coming back reports here instead.
export const TRACKING_ONBOARDING_STEP_REVISITED = 'onboarding_step_revisited';
// The "Which best describes where you're at right now?" step. The screen view
// is reported from FORM_VIEW like every other step; these two say what the
// member did with it.
export const TRACKING_CURRENT_STATUS_SELECTED = 'current_status_selected';
export const TRACKING_CURRENT_STATUS_SKIPPED = 'current_status_skipped';
export const TRACKING_SIGN_UP_SUCCEDEED = 'signup_succeeded';
export const TRACKING_SIGN_UP_FAILED = 'signup_failed';
export const TRACKING_MEMBERSHIP_PAGE = 'choose_membership';
export const TRACKING_STRIPE_PAYMENT_PAGE = 'stripe_payment_page';
export const TRACKING_DOWNLOAD_APP_PAGE = 'download_app_page';
export const TRACKING_PURCHASE_LOCAL_STORAGE_KEY = 'finishedOnboarding';

// Eligibility tracking subjects
export const TRACKING_VISITED_ELIGIBILITY_CHECK = 'check_member_eligibility';
export const TRACKING_CLICK_ELIGIBILITY_CTA = 'click_eligibility_cta';
export const TRACKING_ELIGIBILITY_CHECK_VERIFIED = 'eligibility_check_verified';
export const TRACKING_ELIGIBILITY_CHECK_DENIED = 'eligibility_check_denied';
export const TRACKING_ERROR_ON_ELIGIBILITY_CHECK = 'error_on_eligibility_check';

// Health plan screen tracking subjects
export const TRACKING_HEALTH_PLAN_COLLECTION = 'health_plan_collection';
export const TRACKING_HEALTH_PLAN_SKIPPED = 'health_plan_skipped';

// Unsubscribed landing page tracking subjects
export const TRACKING_VISITED_UNSUBSCRIBED_LANDING =
  'visited_unsubscribed_landing';
export const TRACKING_CLICK_UNSUBSCRIBED_LANDING_CTA =
  'click_unsubscribed_landing_cta';

// Card scan tracking subjects
export const TRACKING_CARD_SCAN_START = 'card_scan_start';
export const TRACKING_CARD_SCAN_SUCCESS = 'card_scan_success';
export const TRACKING_CARD_SCAN_FAILED = 'card_scan_fail';

// Schedule appointment tracking subjects
export const TRACKING_VISITED_SCHEDULE_APPOINTMENT =
  'appointment_scheduler_screen';
export const TRACKING_CLICKED_CTA_TO_BOOK_APPOINTMENT =
  'clicked_cta_to_book_appointment';
export const TRACKING_SUCCESS_BOOK_APPOINTMENT_SCHEDULE =
  'booked_appointment_from_web_onboarding';
export const TRACKING_ERROR_ON_BOOK_APPOINTMENT = 'error_booking_appointment';
export const TRACKING_SKIP_APPOINTMENT_SCHEDULE = 'skip_appointment_schedule';
export const TRACKING_SCROLL_APPOINTMENT_DATES = 'scroll_appointment_dates';
export const TRACKING_SELECT_APPOINTMENT_DATE = 'select_appointment_date';
export const TRACKING_SELECT_APPOINTMENT_TIME = 'select_appointment_time';

// "Is Ayble right for me?" modal in the header
export const TRACKING_SIGNUP_HELP_MODAL_VIEW = 'signup:help_modal_view';

// Feedback screen tracking subject
export const TRACKING_VISITED_SOLICIT_FEEDBACK = 'solicit_feedback_screen';

// Events used for tracking in GTM
export const TRACKING_ONBOARDING_STARTED = 'Onboarding Started';
export const TRACKING_ONBOARDING_COMPLETED = 'Create account';
export const TRACKING_PLANS_SCREEN = 'Select plan';
export const TRACKING_INITIATE_CHECKOUT = 'Go To Checkout';
export const TRACKING_ELIGIBILITY_CHECK_SCREEN = 'Eligibility Check Screen';
export const TRACKING_PURCHASE = 'Thank You';

export enum FormViews {
  landingPage = 'landingPage', // -2
  startOfFlow = 'symptomsList', // 1
  questionGiCondition = 'questionGiCondition', // 2
  questionCurrentStatus = 'questionCurrentStatus', // 3
  questionIdentity = 'questionIdentity', // 4
  endOfFlow = 'accountCreation', // 5
}

// Each question before account creation has its own route, so the browser back
// button steps through them. They sit at the top level like the screens that
// follow them, and `/onboarding` stays the first one because links point at it.
// The landing page keeps `/`, which is where the home route mounts the form.
export const ONBOARDING_ROOT = '/onboarding';
export const HOME_ROOT = '/';

// The two views a visitor may open directly. The rest need the flow walked
// first, or they would be reached with no answers behind them.
export const ENTRY_VIEWS = [FormViews.landingPage, FormViews.startOfFlow];

export const FORM_VIEW_PATH: Partial<Record<FormViews, string>> = {
  [FormViews.landingPage]: HOME_ROOT,
  [FormViews.startOfFlow]: ONBOARDING_ROOT,
  [FormViews.questionGiCondition]: '/conditions',
  [FormViews.questionCurrentStatus]: '/current-status',
  [FormViews.questionIdentity]: '/identity',
  [FormViews.endOfFlow]: '/account-creation',
};

export const FORM_ROUTES = Object.values(FORM_VIEW_PATH).filter(
  (path) => path !== HOME_ROOT
);

export const getViewFromPath = (pathname: string): FormViews | undefined => {
  // Trailing slashes only; an empty result means the home route.
  const normalised = pathname.replace(/\/+$/, '') || HOME_ROOT;

  return (Object.keys(FORM_VIEW_PATH) as FormViews[]).find(
    (view) => FORM_VIEW_PATH[view] === normalised
  );
};

export const FORM_VIEW = {
  [FormViews.landingPage]: {
    label: FormViews.landingPage,
    percentComplete: 15,
    trackingLabel: '1_landing_page',
    styleOverride: { maxWidth: '100%' },
  },
  [FormViews.startOfFlow]: {
    label: FormViews.startOfFlow,
    percentComplete: 30,
    trackingLabel: 'symptoms_questions_list',
  },
  [FormViews.questionGiCondition]: {
    label: FormViews.questionGiCondition,
    percentComplete: 45,
    trackingLabel: '12_gi_symptoms_question',
  },
  [FormViews.questionCurrentStatus]: {
    label: FormViews.questionCurrentStatus,
    percentComplete: 60,
    trackingLabel: 'current_status_question',
  },
  [FormViews.questionIdentity]: {
    label: FormViews.questionIdentity,
    percentComplete: 75,
    trackingLabel: '7_ethnicity_question',
  },
  [FormViews.endOfFlow]: {
    label: FormViews.endOfFlow,
    percentComplete: 90,
    trackingLabel: '18_last_step_page',
    styleOverride: {
      ignoreWhenMobile: true,
      maxWidth: '100%',
      margin: 0,
      padding: 0,
    },
  },
};

export const DESKTOP_BUTTON_WIDTH_PX = 360;
